import { OrgFullDetail, OrgMemberRole, OrgMemberWithUser } from "@humanex/interfaces";
import { Button, Table } from "@humanex/ui/components";
import { useI18n } from "@humanex/ui/libs";
import { Clock, PenLine, Trash, Users } from "lucide-react";
import { useState } from "react";

import { useOrgPendingInvitationsQuery } from "@/hooks/queries";
import { useAuthStore } from "@/stores/auth.store";

import { OrgMemberEditModal } from "./edit-modal";
import { OrgMemberInviteModal } from "./invite-modal";
import { OrgMemberRemoveModal } from "./remove-modal";

interface OrgMemberProps {
  org: OrgFullDetail;
}

export function OrgMember({ org }: OrgMemberProps) {
  const { formatDate, t } = useI18n();
  const { curUser } = useAuthStore();

  const isOwner = org.members.some(
    (member) => member.userId === curUser?.profile?.id && member.role === OrgMemberRole.OWNER
  );

  const { data: pendingInvitations = [] } = useOrgPendingInvitationsQuery(isOwner ? org.id : "");

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{
    userId: string;
    name: string;
    role: OrgMemberRole;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{
    userId: string;
    name: string;
  } | null>(null);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-responsive-lg font-normal text-text-primary">{t("org.members.title")}</p>
          <p className="text-responsive-base font-normal text-text-secondary">{t("org.members.subtitle")}</p>
        </div>
        {isOwner && (
          <Button variant="outline" padding="px-4 py-2" onClick={() => setIsInviteOpen(true)}>
            {t("org.members.inviteMember")}
          </Button>
        )}
      </div>

      {/* Members table / empty state */}
      {org.members.length === 0 ? (
        <div className="w-full py-12 border border-border rounded-lg bg-background-secondary/30">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto bg-background-dark-secondary/10 rounded-full flex items-center justify-center">
                <Users className="w-8 h-8 text-text-secondary" strokeWidth={1.5} />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium text-text-primary">{t("org.members.empty.title")}</p>
              <p className="text-sm text-text-secondary max-w-md">{t("org.members.empty.description")}</p>
            </div>
          </div>
        </div>
      ) : (
        <Table
          data={org.members}
          columns={[
            {
              key: "name",
              title: t("org.members.table.name"),
              dataIndex: "user.name"
            },
            {
              key: "role",
              title: t("org.members.table.role"),
              dataIndex: "role"
            },
            {
              key: "email",
              title: t("org.members.table.email"),
              dataIndex: "user.email"
            },
            {
              key: "phone",
              title: t("org.members.table.phone"),
              dataIndex: "user.phone"
            },
            {
              key: "lastUpdate",
              title: t("org.members.table.lastUpdatedAt"),
              dataIndex: "updatedAt",
              formatter: (value: Date) =>
                formatDate(value, {
                  year: "numeric",
                  month: "short",
                  day: "numeric"
                })
            },
            ...(isOwner
              ? [
                  {
                    key: "actions",
                    title: t("org.members.table.actions"),
                    render: (_value: unknown, record: OrgMemberWithUser) => {
                      const isSelf = record.userId === curUser?.profile?.id;
                      return (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            padding="p-2"
                            onClick={() =>
                              setEditTarget({
                                userId: record.userId,
                                name: record.user.name,
                                role: record.role
                              })
                            }
                          >
                            <PenLine className="w-4 h-4" />
                          </Button>
                          {!isSelf && (
                            <Button
                              variant="ghost"
                              padding="p-2"
                              className="hover:text-error-500"
                              onClick={() =>
                                setRemoveTarget({
                                  userId: record.userId,
                                  name: record.user.name
                                })
                              }
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      );
                    }
                  }
                ]
              : [])
          ]}
        />
      )}

      {/* Pending Invitations */}
      {isOwner && pendingInvitations.length > 0 && (
        <div className="space-y-3">
          <div>
            <p className="text-responsive-base font-medium text-text-primary">
              {t("org.members.pendingInvitations.title")}
            </p>
            <p className="text-sm text-text-secondary">{t("org.members.pendingInvitations.subtitle")}</p>
          </div>
          <Table
            data={pendingInvitations}
            columns={[
              {
                key: "email",
                title: t("org.members.table.email"),
                dataIndex: "email"
              },
              {
                key: "role",
                title: t("org.members.table.role"),
                dataIndex: "role"
              },
              {
                key: "expiresAt",
                title: t("org.members.pendingInvitations.expiresAt"),
                dataIndex: "expiresAt",
                formatter: (value: Date) =>
                  formatDate(value, {
                    year: "numeric",
                    month: "short",
                    day: "numeric"
                  })
              },
              {
                key: "status",
                title: t("org.members.table.status"),
                render: () => (
                  <span className="inline-flex items-center gap-1 text-xs text-warning-600 bg-warning-50 px-2 py-0.5 rounded-full">
                    <Clock className="w-3 h-3" />
                    {t("org.members.pendingInvitations.pending")}
                  </span>
                )
              }
            ]}
          />
        </div>
      )}

      <OrgMemberInviteModal orgId={org.id} isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)} />

      <OrgMemberEditModal orgId={org.id} member={editTarget} onClose={() => setEditTarget(null)} />

      <OrgMemberRemoveModal orgId={org.id} member={removeTarget} onClose={() => setRemoveTarget(null)} />
    </div>
  );
}
